import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { Label } from "@/components/ui/label";

export interface AdditionalFee {
  type: string;
  label: string;
  percentage: number;
}

const FEE_TYPE_OPTIONS = [
  { value: "service_fee", label: "Service Fee" },
  { value: "management_fee", label: "Management Fee" },
  { value: "buffer_fee", label: "Buffer Fee" },
  { value: "other", label: "Other" },
];

interface AdditionalFeesSectionProps {
  fees: AdditionalFee[];
  onChange: (fees: AdditionalFee[]) => void;
  costBeforeMarkup: number;
  currencySymbol: string;
}

export function AdditionalFeesSection({ fees, onChange, costBeforeMarkup, currencySymbol }: AdditionalFeesSectionProps) {
  const addFee = () => {
    onChange([...fees, { type: "service_fee", label: "Service Fee", percentage: 0 }]);
  };

  const removeFee = (index: number) => {
    onChange(fees.filter((_, i) => i !== index));
  };

  const updateFee = (index: number, updates: Partial<AdditionalFee>) => {
    const updated = fees.map((fee, i) => {
      if (i !== index) return fee;
      const merged = { ...fee, ...updates };
      // Auto-set label from type unless it's "other"
      if (updates.type && updates.type !== "other") {
        const option = FEE_TYPE_OPTIONS.find(o => o.value === updates.type);
        merged.label = option?.label || merged.label;
      }
      return merged;
    });
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Additional Fees</Label>
        <Button type="button" variant="outline" size="sm" onClick={addFee} className="gap-1 h-7 text-xs">
          <Plus className="h-3 w-3" />
          Add Fee
        </Button>
      </div>

      {fees.map((fee, index) => {
        const amount = costBeforeMarkup * (fee.percentage / 100);
        return (
          <div key={index} className="flex items-center gap-2">
            <Select
              value={fee.type}
              onValueChange={(val) => updateFee(index, { type: val })}
            >
              <SelectTrigger className="w-[160px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FEE_TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fee.type === "other" && (
              <Input
                className="w-[120px] h-9"
                placeholder="Label"
                value={fee.label}
                onChange={(e) => updateFee(index, { label: e.target.value })}
              />
            )}
            <div className="flex items-center gap-1 flex-1">
              <Input
                type="number"
                step="0.01"
                min="0"
                className="h-9 w-20"
                value={fee.percentage || ""}
                onChange={(e) => updateFee(index, { percentage: parseFloat(e.target.value) || 0 })}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <span className="text-sm text-muted-foreground whitespace-nowrap min-w-[80px] text-right">
              {currencySymbol}{amount.toLocaleString()}
            </span>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeFee(index)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export function calculateAdditionalFeesTotal(fees: AdditionalFee[], costBeforeMarkup: number): number {
  return fees.reduce((sum, fee) => sum + costBeforeMarkup * (fee.percentage / 100), 0);
}

export function serializeAdditionalFees(fees: AdditionalFee[], costBeforeMarkup: number) {
  return fees
    .filter(f => f.percentage > 0)
    .map(f => ({
      type: f.type,
      label: f.label,
      percentage: f.percentage,
      amount: costBeforeMarkup * (f.percentage / 100),
    }));
}
